import type { ReviewRun } from '@/data/reviews/use-review-statuses';

import { extractAiAgentPromptText } from './pr-v2-comment-utils';

import { isTimedOutPendingRun } from '@/data/reviews/review-run-optimistic';
import { isMrgeBot } from '@/utils/github/bot-detection';

// We allow 10 minutes of drift to tolerate async GitHub webhooks and delayed review-run writes
// while still associating prompt-copy metadata with the correct run window.
const RUN_PROMPT_TIME_DRIFT_MS = 10 * 60 * 1000;

export type ReviewPromptSource = {
  id: number;
  body?: string | null;
  submitted_at?: string | null;
  created_at?: string | null;
  user?: {
    login?: string | null;
  } | null;
};

export type GeneralReviewRunForPromptCopy = Pick<
  ReviewRun,
  'status' | 'violationCount' | 'createdAt' | 'updatedAt'
>;

function toTimestamp(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function compareReviewRunsByRecency(a: ReviewRun, b: ReviewRun): number {
  const updatedAtDiff = toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt);
  if (updatedAtDiff !== 0) {
    return updatedAtDiff;
  }

  return toTimestamp(b.createdAt) - toTimestamp(a.createdAt);
}

const isRunningForDisplay = (run: ReviewRun): boolean =>
  run.status === 'running' && !isTimedOutPendingRun(run);

function shouldPreferRun(candidate: ReviewRun, current: ReviewRun): boolean {
  if (isRunningForDisplay(candidate) && !isRunningForDisplay(current)) {
    return true;
  }

  if (!isRunningForDisplay(candidate) && isRunningForDisplay(current)) {
    return false;
  }

  return compareReviewRunsByRecency(candidate, current) < 0;
}

/**
 * Picks the general/batch run that best represents prompt-copy state.
 * Active running rows are preferred over terminal rows; otherwise latest update wins.
 */
export function selectGeneralReviewRunForPromptCopy(
  reviewRuns: ReviewRun[] | undefined,
): ReviewRun | undefined {
  let generalReviewRun: ReviewRun | undefined;

  for (const run of reviewRuns ?? []) {
    if (run.ruleId) {
      continue;
    }

    if (!generalReviewRun || shouldPreferRun(run, generalReviewRun)) {
      generalReviewRun = run;
    }
  }

  return generalReviewRun;
}

export function selectLatestCubicReviewPrompt(
  reviews?: ReviewPromptSource[],
  generalReviewRun?: GeneralReviewRunForPromptCopy,
): string | null {
  const runCreatedAt = toTimestamp(generalReviewRun?.createdAt);
  const runUpdatedAt = toTimestamp(generalReviewRun?.updatedAt);
  const runTimestamps = [runCreatedAt, runUpdatedAt].filter((value) => value > 0);
  const hasRunWindow = runTimestamps.length > 0;
  const runWindowStart = hasRunWindow ? Math.min(...runTimestamps) : 0;
  const runWindowEnd = hasRunWindow ? Math.max(...runTimestamps) : 0;

  let latestPromptInRunWindow: string | null = null;
  let latestPromptInRunWindowTimestamp = -1;
  let latestPromptInRunWindowReviewId = -1;
  let latestPromptOverall: string | null = null;
  let latestPromptOverallTimestamp = -1;
  let latestPromptOverallReviewId = -1;

  for (const review of reviews ?? []) {
    if (!isMrgeBot(review.user?.login)) {
      continue;
    }

    const promptText = extractAiAgentPromptText(review.body);
    if (!promptText) {
      continue;
    }

    const timestamp = Math.max(toTimestamp(review.submitted_at), toTimestamp(review.created_at));
    const shouldReplace =
      timestamp > latestPromptOverallTimestamp ||
      (timestamp === latestPromptOverallTimestamp && review.id > latestPromptOverallReviewId);
    if (shouldReplace) {
      latestPromptOverall = promptText;
      latestPromptOverallTimestamp = timestamp;
      latestPromptOverallReviewId = review.id;
    }

    if (
      hasRunWindow &&
      (timestamp < runWindowStart - RUN_PROMPT_TIME_DRIFT_MS ||
        timestamp > runWindowEnd + RUN_PROMPT_TIME_DRIFT_MS)
    ) {
      continue;
    }

    const shouldReplaceInRunWindow =
      timestamp > latestPromptInRunWindowTimestamp ||
      (timestamp === latestPromptInRunWindowTimestamp &&
        review.id > latestPromptInRunWindowReviewId);
    if (shouldReplaceInRunWindow) {
      latestPromptInRunWindow = promptText;
      latestPromptInRunWindowTimestamp = timestamp;
      latestPromptInRunWindowReviewId = review.id;
    }
  }

  return latestPromptInRunWindow ?? latestPromptOverall ?? null;
}

export function shouldShowGeneralReviewPromptCopyAction(
  generalReviewRun: GeneralReviewRunForPromptCopy | undefined,
  promptText: string | null,
): boolean {
  return (
    generalReviewRun?.status === 'completed' &&
    generalReviewRun.violationCount > 0 &&
    Boolean(promptText)
  );
}
